import React from 'react';
import PropTypes from 'prop-types';

/**
 * Pieces for a table whose rows can be dragged into order (`common/useSortable`),
 * drawn the old admin's way: inline styles, no stylesheet of their own.
 */

const DROP_LINE = '#2b6cb0';

/**
 * A text action next to the arrows (edit, remove): padded so its click area is
 * bigger than the word, and spaced so it is hard to hit the one beside it.
 */
export const rowActionStyle = (danger = false) => ({
  cursor: 'pointer',
  display: 'inline-block',
  padding: '4px 6px',
  marginRight: 16,
  color: danger ? '#c0392b' : undefined
});

/** Style for a sortable `<tr>`: faded while dragged, a line where it will land. */
export function sortableRowStyle(sortable, item) {
  const side = sortable.dropSide(item);
  return {
    opacity: sortable.isDragging(item) ? 0.45 : 1,
    boxShadow: side === 'before'
      ? `inset 0 3px 0 ${DROP_LINE}`
      : side === 'after' ? `inset 0 -3px 0 ${DROP_LINE}` : undefined
  };
}

/** The grip that starts a drag, followed by the row's position. */
export function SortHandle({ sortable, item, position }) {
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      <span
        title={sortable.enabled ? 'Drag to reorder' : 'Reordering is off right now'}
        style={{
          cursor: sortable.enabled ? 'grab' : 'not-allowed',
          opacity: sortable.enabled ? 1 : 0.35,
          fontSize: 16,
          userSelect: 'none',
          paddingRight: 6
        }}
        {...sortable.handleProps(item)}
      >
        ⠿
      </span>
      <span style={{ color: 'grey' }}>{position}</span>
    </span>
  );
}

SortHandle.propTypes = {
  sortable: PropTypes.object.isRequired,
  item: PropTypes.object.isRequired,
  position: PropTypes.number.isRequired
};

/** The up/down buttons: the same reordering without a mouse, and on touch screens. */
export function MoveButtons({ sortable, item, index, count }) {
  const style = { padding: '0 6px', marginRight: 8 };
  return (
    <span style={{ whiteSpace: 'nowrap', marginRight: 24 }}>
      <button
        type="button"
        className="button button-color-secondary"
        style={style}
        disabled={!sortable.enabled || index === 0}
        onClick={() => sortable.move(sortable.idOf(item), -1)}
        aria-label="Move up"
      >
        ↑
      </button>
      <button
        type="button"
        className="button button-color-secondary"
        style={style}
        disabled={!sortable.enabled || index === count - 1}
        onClick={() => sortable.move(sortable.idOf(item), 1)}
        aria-label="Move down"
      >
        ↓
      </button>
    </span>
  );
}

MoveButtons.propTypes = {
  sortable: PropTypes.object.isRequired,
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired
};
